import { useAuthStore, hostRole, pendingFromSearch, savePendingHandoff } from '@sentinel/web-core';
import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
// The round trip between hosts stays in the first chunk: it runs before any page is shown.
// Every other page is its own chunk, loaded when it is opened.
import { HostCallbackPage } from '@/modules/auth/hosts/HostCallbackPage';
import { HostStartPage } from '@/modules/auth/hosts/HostStartPage';
import { rootRoute } from './root-routes';

const tokenSearch = (search: Record<string, unknown>) => ({
  token: typeof search['token'] === 'string' ? search['token'] : '',
});

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('@/modules/auth/login/LoginPage'), 'LoginPage'),
  // Return from Google (ADR 0014): a one-time code, or the reason it did not work.
  validateSearch: (search: Record<string, unknown>): { sso?: string; sso_error?: string } => {
    const { sso, sso_error: reason } = search;
    // Three hosts (ADR 0013): the app that sent the person here, kept for after the sign-in.
    const pending = pendingFromSearch(search);
    if (pending !== null) savePendingHandoff(pending);
    return {
      ...(typeof sso === 'string' ? { sso } : {}),
      ...(typeof reason === 'string' ? { sso_error: reason } : {}),
    };
  },
  beforeLoad: () => {
    const { status, me } = useAuthStore.getState();
    const role = hostRole();
    // Only `accounts.` signs in: the other two apps start the round trip from their own page.
    if (role === 'console' || role === 'control') throw redirect({ to: '/auth/start' });
    if (status === 'authenticated' && role === 'accounts') throw redirect({ to: '/handoff' });
    if (status === 'authenticated')
      throw redirect({
        to: me?.context === 'console' ? '/platform' : '/',
      });
  },
});

export const forgotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: lazyRouteComponent(() => import('@/modules/auth/PasswordPages'), 'ForgotPasswordPage'),
});

export const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: lazyRouteComponent(() => import('@/modules/auth/PasswordPages'), 'ResetPasswordPage'),
  validateSearch: tokenSearch,
});

export const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation',
  component: lazyRouteComponent(
    () => import('@/modules/auth/AcceptInvitationPage'),
    'AcceptInvitationPage',
  ),
  validateSearch: tokenSearch,
});

/** Three hosts (ADR 0013): the round trip through `accounts.` and back. */
export const hostStartRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/start',
  component: HostStartPage,
  validateSearch: (search: Record<string, unknown>): { org?: string; grant?: string } => ({
    ...(typeof search['org'] === 'string' ? { org: search['org'] } : {}),
    ...(typeof search['grant'] === 'string' ? { grant: search['grant'] } : {}),
  }),
});

export const hostCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: HostCallbackPage,
  validateSearch: (search: Record<string, unknown>): { code?: string } =>
    typeof search['code'] === 'string' ? { code: search['code'] } : {},
});

export const handoffRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/handoff',
  component: lazyRouteComponent(() => import('@/modules/auth/hosts/HandoffPage'), 'HandoffPage'),
  beforeLoad: () => {
    if (useAuthStore.getState().status !== 'authenticated') throw redirect({ to: '/login' });
  },
});

export const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: lazyRouteComponent(() => import('@/modules/auth/hosts/LogoutPage'), 'LogoutPage'),
});

/** The page DJI Pilot 2 opens on a drone's controller (ADR 0017): no session, any host (the
 *  controller reaches the server by its address on the network). */
export const pilotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dji',
  component: lazyRouteComponent(() => import('@/modules/drones/pilot/PilotPage'), 'PilotPage'),
});
